import React from 'react';
import { isEmpty } from 'lodash';
import { Autocomplete, TextField } from '@mui/material';
import Button from 'components/Button/Button';
import { getAppOptions } from 'components/BoostedOffers/helpers';
import { useGetUserApplicationsQuery } from 'reduxState/store/application/api';
import { campaignTypes } from './constants';
import { IAppOption, TCampaignTypes } from './types';

interface IActionRewardsFiltersProps {
  appIds: IAppOption[];
  setAppIds: (appIds: IAppOption[]) => void;
  campaignId: number;
  setCampaignId: (campaignId: number) => void;
  actionReward: TCampaignTypes | '';
  setActionReward: (actionReward: TCampaignTypes | '') => void;
  onApply: () => void;
  onClear: () => void;
}

/**
 * ActionRewardsFilters - A filter component for managing action rewards search
 *
 * This component provides filtering interface for action rewards, allowing users to:
 * - Select multiple applications to include in the search
 * - Filter by specific campaign ID
 * - Filter by campaign type (action reward type)
 * - Apply or clear all filter selections
 *
 *
 * @component
 * @param {Object} props - The component props
 * @param {IAppOption[]} props.appIds - Array of currently selected application options
 * @param {Function} props.setAppIds - Callback function to update the selected application IDs
 * @param {number} props.campaignId - Currently selected campaign ID (0 indicates no selection)
 * @param {Function} props.setCampaignId - Callback function to update the campaign ID filter
 * @param {TCampaignTypes | ''} props.actionReward - Currently selected campaign type filter
 * @param {Function} props.setActionReward - Callback function to update the action reward type filter
 * @param {Function} props.onApply - Callback function triggered when the Apply button is clicked
 * @param {Function} props.onClear - Callback function triggered when the Clear button is clicked
 *
 * @example
 * <ActionRewardsFilters
 *   appIds={appIds}
 *   setAppIds={setAppIds}
 *   campaignId={campaignId}
 *   setCampaignId={setCampaignId}
 *   actionReward={actionReward}
 *   setActionReward={setActionReward}
 *   onApply={handleApply}
 *   onClear={handleClear}
 * />
 *
 * @returns {JSX.Element} A responsive filter panel with application selection, campaign ID input,
 *                        campaign type selection, and action buttons
 */
const ActionRewardsFilters = ({
  appIds,
  setAppIds,
  campaignId,
  setCampaignId,
  actionReward,
  setActionReward,
  onApply,
  onClear,
}: IActionRewardsFiltersProps) => {
  const { data: applicationsData } = useGetUserApplicationsQuery();
  const applications = applicationsData?.applications || [];

  return (
    <div className="max-w-[1400px] w-full h-fit flex flex-col font-montserrat bg-white rounded-md p-6 shadow-lg mb-4">
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <Autocomplete
          multiple
          disableCloseOnSelect
          className="w-full lg:grow bg-white rounded-md max-height-full overflow-auto font-montserrat lg:min-w-[250px] [&>div>div>input]:!font-montserrat [&>div>div>input]:!text-sm [&>div>div>input]:!font-montserrat [&>div>div]:!rounded-md"
          value={appIds}
          options={getAppOptions(applications)}
          isOptionEqualToValue={(option, value) => option.id === value.id} // To prevent from getting a MUI warning when option and value are pointing to different instances
          onChange={(_, value) => setAppIds(value)}
          sx={{
            '& .MuiAutocomplete-tag': {
              background: '#5c5387',
              color: '#fff',
              fontSize: '11px',

              '& .MuiChip-deleteIcon': {
                color: '#fff',
                opacity: '0.7',

                '&:hover': {
                  color: '#fff',
                  opacity: '0.9',
                },
              },
            },
          }}
          renderInput={params => (
            <TextField
              {...params}
              className="flex items-center flex-row font-montserrat shadow-md rounded-md"
              variant="outlined"
              placeholder="Applications to Include"
            />
          )}
        />
        <TextField
          className="w-full lg:min-w-[150px] lg:w-auto [&>div>input]:!font-montserrat [&>div>input]:!text-sm [&>div>input]:!py-[16.5px]"
          value={campaignId === 0 ? '' : campaignId}
          onChange={e => {
            // Only allow numbers in the input
            const value = e.target.value.replace(/[^0-9]/g, '');
            setCampaignId(Number(value));
          }}
          placeholder="Campaign ID"
        />
        <Autocomplete
          className="w-full lg:min-w-[200px] lg:w-auto [&>div>div>input]:!font-montserrat [&>div>div>input]:!text-sm [&>div>div]:!rounded-md"
          value={isEmpty(actionReward) ? null : campaignTypes.find(type => type.value === actionReward)}
          options={campaignTypes}
          onChange={(_, data) => setActionReward((data?.value as TCampaignTypes) || '')}
          renderInput={params => <TextField {...params} placeholder="Campaign Type" />}
        />
        <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
          <Button onClick={onApply} className="purple-rounded-button px-7 py-[16.5px] font-semibold !text-sm">
            APPLY
          </Button>
          <Button onClick={onClear} className="grey-rounded-button px-3 py-[16.5px] font-semibold !text-sm">
            CLEAR
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActionRewardsFilters;
